import React from 'react';
import connect from "react-redux/es/connect/connect";
import {injectIntl} from "react-intl";
import {Modal} from "antd";
import AvailabilityCalendar from "./AvailabilityCalendar";
import LoadingIndicator from "../../common/loading/LoadingIndicator";
import moment from 'moment';
import Constants from "../../../services/Constants";
import Time from "../../../services/Time";
import AvailabilityStart from "./AvailabilityStart";
import AvailabilityEnd from "./AvailabilityEnd";
import Intervention from "../../../services/Intervention";
import InterventionService from "../../../services/Intervention";

const END_SLOTS_INTERVAL = 5;  // minutes
const END_DATE_INTERVAL = 3;    // months
const AVAILABLE_CALENDAR_DAY_COUNT = moment.duration(END_DATE_INTERVAL, 'months').asDays();

class AvailabilityPlanning extends React.Component {

  constructor(props) {
    super(props);
    this.state = this.initDates();
  }

  initDates = () => {
    const {intervention} = this.props;
    const duringWorkingHours = Intervention.duringWorkingHours(intervention);
    return {
      startDate: Time.dateTimeToDateForApi(intervention.startDate),
      startTime: Time.dateTimeToTimeForApi(intervention.startDate),
      endDate: Time.dateTimeToDateForApi(intervention.endDate),
      endTime: Time.dateTimeToTimeForApi(intervention.endDate),
      duringWorkingHours,
    }
  };

  componentDidMount() {
    const {intervention, intervention: {journey: {properties = {}} = {}, id}} = this.props;
    const {services} = properties.services;
    const {mobility} = properties.mobility;
    const oas = InterventionService.isOAS(intervention);

    this.props.dispatchFetchOpeningHours();
    const bookingToken = properties.services.valueServicesCart && properties.services.valueServicesCart.bookingToken;
    this.props.dispatchFetchAvailabilities(id, {services: services.map(s => oas ? s.id : s.name), mobility: mobility.name, bookingToken});
  }

  isTwentyFourSevenAllowed() {
    const {intervention} = this.props;
    if (!Intervention.isTypeAppointment(intervention) || Intervention.isWeb(intervention)) {
      return false;
    }
    const {intervention: {journey: {properties = {}}}, mainServices: {offering: {rentalPickup}}} = this.props;
    const {mobility} = properties.mobility;
    return !Constants.MOBILITY_VEHICLES.includes(mobility.name) || rentalPickup;
  };

  isTwentyFourSevenAllowedForCurrentDay(intervention, twentyFourSevenAllowed, availability) {
    const initialDay = moment(intervention.startDate).format(Constants.DATE_FORMAT_API);
    return (twentyFourSevenAllowed && availability.twentyFourSevenAvailable) || (availability.day === initialDay && !Intervention.duringWorkingHours(intervention))
  }

  computeAvailableAvailabilities() {
    const {intervention} = this.props;
    const twentyFourSevenAllowed = this.isTwentyFourSevenAllowed();
    return (this.props.availabilities || []).filter(availability => {
      return availability.availableSlots.length || this.isTwentyFourSevenAllowedForCurrentDay(intervention, twentyFourSevenAllowed, availability);
    });
  }

  computeAvailableEndDates(){
    const {intervention} = this.props;
    const endDate = Time.dateTimeToDateForApi(intervention.endDate);
    const endTime = Time.dateTimeToTimeForApi(intervention.endDate);
    // compute the end dates and slots
    const availabilities = this.props.availabilities || [];
    if (!availabilities.length){
      return [];
    }
    const lowerDateBoundary = availabilities.map(availability=>availability.day).sort()[0];

    const {dealerOpeningHours = {}} = this.props.fetchOpeningHours;
    let endDates = [];
    if (dealerOpeningHours) {
      for (let index = 0; index <= AVAILABLE_CALENDAR_DAY_COUNT; index++) {
        const d = moment(lowerDateBoundary).add(index, 'days');
        let schedule = dealerOpeningHours[Time.dayOfWeekAsStringUppercase(d)];
        if (schedule && schedule.slots && schedule.slots.length > 0) {
          const opening = moment(schedule.slots[0].openTime, Constants.TIME_FORMAT_API);
          const closing = moment(schedule.slots[schedule.slots.length - 1].closeTime, Constants.TIME_FORMAT_API);
          let slots = [];
          for (let time = opening; time.isSameOrBefore(closing); time.add(END_SLOTS_INTERVAL, 'minutes')){
            slots.push(time.format(Constants.TIME_FORMAT_API));
          }

          // If the end time does ot exists anymore (planing has been updated in the meantime), we need to add it so we can still display it
          if (d.format(Constants.DATE_FORMAT_API) === endDate && !slots.includes(endTime)) {
            slots.push(endTime);
          }

          // We should not propose a end date/time === to the start date/time
          if (d.format(Constants.DATE_FORMAT_API) === this.state.startDate) {
            slots = slots.filter(time => time !== this.state.startTime);
          }

          slots.sort((a, b)=>Time.timeComparator(a, b));
          endDates.push({
            day: d.format(Constants.DATE_FORMAT_API),
            slots
          })
        } else if (d.format(Constants.DATE_FORMAT_API) === endDate){
          endDates.push({
            day: d.format(Constants.DATE_FORMAT_API),
            slots:[endTime],
          })
        }
      }
    }
    return endDates;
  }

  computeAvailableStartDates(){
    const {intervention} = this.props;
    const twentyFourSevenAllowed = this.isTwentyFourSevenAllowed();

    const startDate = Time.dateTimeToDateForApi(intervention.startDate);
    const startTime = Time.dateTimeToTimeForApi(intervention.startDate);
    // compute the start dates and slots
    const availabilities = this.props.availabilities || [];
    return availabilities
      .filter(availability => availability.day === startDate || availability.status === Constants.DAY_OPEN.value || availability.status === Constants.DAY_OVERBOOKED.value)
      .map(availability => {
        const {day, availableSlots = []} = availability;
        const slots = availableSlots.map(slot => slot.startTime);
        if (day === startDate && !slots.includes(startTime) && Intervention.duringWorkingHours(intervention)) {
          // Add the actual start time if it was not present initially in the availabilities
          slots.push(startTime);
        }
        slots.sort((a, b) => Time.timeComparator(a, b));
        return {
          day,
          twentyFourSevenAvailable: this.isTwentyFourSevenAllowedForCurrentDay(intervention, twentyFourSevenAllowed, availability),
          slots
        };
      })
      .filter(availability => availability.twentyFourSevenAvailable || availability.slots.length);
  }

  onSave = () => {
    const {intervention} = this.props;
    const {startDate, startTime, endDate, endTime, duringWorkingHours} = this.state;

    this.props.dispatchConfirmAppointment({
      id: intervention.id,
      dates: {
        startDate: startDate,
        startTime: startTime,
        endDate: endDate,
        endTime: endTime,
        duringWorkingHours: duringWorkingHours
      }
    });
  };

  onDateStartDateChange = (e) => {
    const {duringWorkingHours} = this.state;
    const selectedDay = this.computeAvailableStartDates().find(d=>d.day === e.target.value);
    // Try to find the same element, or else take the first available
    const startTime = selectedDay.slots.find(slot=>slot >= this.state.startTime) || selectedDay.slots[0];

    this.setState({
      [e.target.name]: e.target.value,
      startTime: startTime,
      duringWorkingHours: selectedDay.twentyFourSevenAvailable ? duringWorkingHours : true // set duringWorkingHours=true to prevent old values from staying
    });
  };

  onDateEndDateChange = (e) => {
    const selectedDay = this.computeAvailableEndDates().find(d=>d.day === e.target.value);
    // Try to find the same element, or else take the first available
    const endTime = selectedDay.slots.find(slot=>slot >= this.state.endTime) || selectedDay.slots[0];

    this.setState({
      [e.target.name]: e.target.value,
      endTime: endTime,
    });
  };

  onTimeChange = (e) => {
    this.setState({[e.target.name]: e.target.value});
  };

  onTwentyFourChange = (e) => {
    const startDates = this.computeAvailableStartDates();
    const startTime = startDates.find(d=>d.day === this.state.startDate).slots[0];
    this.setState({[e.target.name]: !e.target.checked, startTime});
  };

  render() {
    const {intl:{formatMessage}, intervention, visible, onClose} = this.props;
    const {validationErrors, loading, serviceDuration, fetchOpeningHours: {dealerOpeningHours}} = this.props;
    const isConfirmed = Intervention.isConfirmed(intervention);
    const isRentalAssigned = intervention.journey.rentalVehicle;

    const filteredAvailabilities = this.computeAvailableAvailabilities();
    const startDates = this.computeAvailableStartDates();
    const endDates = this.computeAvailableEndDates();

    return (
      <Modal title={formatMessage({id: 'appointment.detail.modal.planning'})}
             visible={visible}
             onOk={this.onSave}
             onCancel={onClose}>
        {
          (loading) ? (
            <LoadingIndicator/>
          ) : (
            <div>
              {isConfirmed && isRentalAssigned &&
                <div className="alert alert--error">{formatMessage({id: 'warning.appointment.confirmed.dates'})}</div>
              }

              <AvailabilityCalendar intervention={this.props.intervention} availabilities={filteredAvailabilities}/>

              <br/>
              <div className="table-status--error">
                {serviceDuration && `${formatMessage({id: 'placeholder.duration'})} : ${Time.durationToMoment(serviceDuration, 'HH:mm').format(Constants.TIME_FORMAT_DISPLAY)}`}
              </div>
              <br/>

              <AvailabilityStart availabilities={startDates}
                                 intervention={intervention}
                                 selectedDates={this.state}
                                 onDateChange={this.onDateStartDateChange}
                                 onTimeChange={this.onTimeChange}
                                 onTwentyFourChange={this.onTwentyFourChange}
                                 error={validationErrors}/>

              <AvailabilityEnd availabilities={endDates}
                               openingHours={dealerOpeningHours}
                               intervention={intervention}
                               selectedDates={this.state}
                               onDateChange={this.onDateEndDateChange}
                               onTimeChange={this.onTimeChange}
                               error={validationErrors}/>
              <div className="alert alert--warn">{formatMessage({id: 'warning.appointment.changing.dates'})}</div>
            </div>
          )
        }
      </Modal>
    )
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    validationErrors : state.fetchIntervention && state.fetchIntervention.error && state.fetchIntervention.error.content,
    loading: state.fetchAvailabilities.loading,
    availabilities: state.fetchAvailabilities.availabilities,
    serviceDuration: state.fetchAvailabilities.serviceDuration,
    fetchOpeningHours: state.fetchOpeningHours.openingHours,
  };
};

const mapDispatchToProps = dispatch => ({
  dispatchFetchAvailabilities: (id, services) => dispatch({type: 'FETCH_AVAILABILITIES.REQUEST', id, services}),
  dispatchFetchOpeningHours: () => dispatch({type: 'FETCH_OPENING_HOURS.REQUEST'}),
  dispatchConfirmAppointment: (body) => dispatch({type: 'SAVE.INTERVENTION_CONFIRMATION.REQUEST', body})
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(AvailabilityPlanning))



// WEBPACK FOOTER //
// ./src/components/interventiondetail/planning/AvailabilityPlanning.js